import type { ILanguageResources, ITranslationModule } from '../../../../interfaces';
import { interpolate } from '../../../../global';

// Column Titles, Table Titles, Dialogs, UI Strings
class Id {
  public static readonly prefix = 'UI.scripts.dealer-franchisee.quotes.views.FranchiseeQuoteListView.';
  // Table column headers
  public static readonly quoteNumberTitle = `${Id.prefix}quoteNumberTitle`;
  public static readonly quoteTitle = `${Id.prefix}quoteTitle`;
  public static readonly projectTitle = `${Id.prefix}projectTitle`;
  public static readonly clientTitle = `${Id.prefix}clientTitle`;
  public static readonly authorTitle = `${Id.prefix}authorTitle`;
  public static readonly lastModifiedTitle = `${Id.prefix}lastModifiedTitle`;
  public static readonly statusTitle = `${Id.prefix}statusTitle`;
  public static readonly netAmountTitle = `${Id.prefix}netAmountTitle`;
  // Advanced filter radio
  public static readonly allQuotesRadio = `${Id.prefix}allQuotesRadio`;
  public static readonly myQuotesRadio = `${Id.prefix}myQuotesRadio`;
  // Dialogs
  public static readonly deleteQuoteConfirmation = `${Id.prefix}deleteQuoteConfirmation`;
  // Table section titles
  public static readonly allCurrentTableTitle = `${Id.prefix}allCurrentTableTitle`;
  public static readonly draftTableTitle = `${Id.prefix}draftTableTitle`;
  public static readonly activeTableTitle = `${Id.prefix}activeTableTitle`;
  public static readonly issuedTableTitle = `${Id.prefix}issuedTableTitle`;
  public static readonly acceptedTableTitle = `${Id.prefix}acceptedTableTitle`;
  public static readonly underReviewTableTitle = `${Id.prefix}underReviewTableTitle`;
  public static readonly reviewedTableTitle = `${Id.prefix}reviewedTableTitle`;
  public static readonly archivedTableTitle = `${Id.prefix}archivedTableTitle`;
  // Add for Discard button
  public static readonly discardButton = `${Id.prefix}discardButton`;

  // Toasts (commented out, add if you use them)
  // public static readonly quotesRefreshedToast = `${Id.prefix}quotesRefreshedToast`;
  // public static readonly updatesComingSoonToast = `${Id.prefix}updatesComingSoonToast`;
  // public static readonly moreUpdatesComingToast = `${Id.prefix}moreUpdatesComingToast`;
  public static readonly quoteCopiedSuccessfully = `${Id.prefix}quoteCopiedSuccessfully`;
  public static readonly quoteCopyFailed = `${Id.prefix}quoteCopyFailed`;
  public static readonly copyButton = `${Id.prefix}copyButton`;
  public static readonly deleteButton = `${Id.prefix}deleteButton`;
  public static readonly termsButton = `${Id.prefix}termsButton`;
  public static readonly termsPickButton = `${Id.prefix}termsPickButton`;
  public static readonly termsClearButton = `${Id.prefix}termsClearButton`;
  public static readonly viewQuote = `${Id.prefix}viewQuote`;
  // Tab titles
  public static readonly quoteDefaultsTab = `${Id.prefix}quoteDefaultsTab`;

  private constructor() {}
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class LangFranchiseeQuoteListView {
  private constructor() {}

  // Table column headers
  public static quoteNumberTitle(): string {
    return interpolate(Id.quoteNumberTitle);
  }

  public static quoteTitle(): string {
    return interpolate(Id.quoteTitle);
  }

  public static projectTitle(): string {
    return interpolate(Id.projectTitle);
  }

  public static clientTitle(): string {
    return interpolate(Id.clientTitle);
  }

  public static authorTitle(): string {
    return interpolate(Id.authorTitle);
  }

  public static lastModifiedTitle(): string {
    return interpolate(Id.lastModifiedTitle);
  }

  public static statusTitle(): string {
    return interpolate(Id.statusTitle);
  }

  public static netAmountTitle(): string {
    return interpolate(Id.netAmountTitle);
  }

  // Advanced filter radio
  public static allQuotesRadio(): string {
    return interpolate(Id.allQuotesRadio);
  }

  public static myQuotesRadio(): string {
    return interpolate(Id.myQuotesRadio);
  }

  // Dialogs
  public static deleteQuoteConfirmation(): string {
    return interpolate(Id.deleteQuoteConfirmation);
  }

  // Table section titles
  public static allCurrentTableTitle(): string {
    return interpolate(Id.allCurrentTableTitle);
  }

  public static draftTableTitle(): string {
    return interpolate(Id.draftTableTitle);
  }

  public static activeTableTitle(): string {
    return interpolate(Id.activeTableTitle);
  }

  public static issuedTableTitle(): string {
    return interpolate(Id.issuedTableTitle);
  }

  public static acceptedTableTitle(): string {
    return interpolate(Id.acceptedTableTitle);
  }

  public static underReviewTableTitle(): string {
    return interpolate(Id.underReviewTableTitle);
  }

  public static reviewedTableTitle(): string {
    return interpolate(Id.reviewedTableTitle);
  }

  public static archivedTableTitle(): string {
    return interpolate(Id.archivedTableTitle);
  }

  // Toasts (add if you use them)
  // public static quotesRefreshedToast(): string {
  //   return interpolate(Id.quotesRefreshedToast);
  // }
  // public static updatesComingSoonToast(): string {
  //   return interpolate(Id.updatesComingSoonToast);
  // }
  // public static moreUpdatesComingToast(): string {
  //   return interpolate(Id.moreUpdatesComingToast);
  // }

  public static quoteCopiedSuccessfully(quoteNumber: string, quoteTitle: string): string {
    return interpolate(Id.quoteCopiedSuccessfully, { quoteNumber, quoteTitle });
  }

  public static quoteCopyFailed(quoteNumber: string, quoteTitle: string, errorMessage: string): string {
    return interpolate(Id.quoteCopyFailed, { quoteNumber, quoteTitle, errorMessage });
  }

  // Add for Discard button
  public static discardButton(): string {
    return interpolate(Id.discardButton);
  }

  public static copyButton(): string {
    return interpolate(Id.copyButton);
  }

  public static deleteButton(): string {
    return interpolate(Id.deleteButton);
  }

  public static termsButton(): string {
    return interpolate(Id.termsButton);
  }

  public static termsPickButton(): string {
    return interpolate(Id.termsPickButton);
  }

  public static termsClearButton(): string {
    return interpolate(Id.termsClearButton);
  }

  public static viewQuote(): string {
    return interpolate(Id.viewQuote);
  }

  // Tab titles
  public static quoteDefaultsTab(): string {
    return interpolate(Id.quoteDefaultsTab);
  }
}

export class FranchiseeQuoteListViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // Table column headers
    register.add(Id.quoteNumberTitle, "{{glossary 'quote'}} No.", 'Column header for Quote Number');
    register.add(Id.quoteTitle, "{{glossary 'quote'}} Title", 'Column header for Quote Title');
    register.add(Id.projectTitle, "{{glossary 'project'}} Title", 'Column header for Project Title');
    register.add(Id.clientTitle, "{{glossary 'client'}}", 'Column header for Client');
    register.add(Id.authorTitle, "{{glossary 'author'}}", 'Column header for Author');
    register.add(Id.lastModifiedTitle, 'Last Modified', 'Column header for Last Modified');
    register.add(Id.statusTitle, 'Status', 'Column header for Status');
    register.add(Id.netAmountTitle, 'Net Amount', 'Column header for Net Amount');

    // Advanced filter radio
    register.add(Id.allQuotesRadio, "All {{glossary 'quote:plural'}}", 'Radio button for all quotes');
    register.add(Id.myQuotesRadio, "My {{glossary 'quote:plural'}}", 'Radio button for my quotes');

    // Dialogs
    register.add(
      Id.deleteQuoteConfirmation,
      "Are you sure you'd like to delete the {{glossary 'quote'}}?",
      'Confirmation dialog for deleting a quote'
    );

    // Table section titles
    register.add(Id.allCurrentTableTitle, 'All Current', 'Table section title for all current quotes');
    register.add(Id.draftTableTitle, 'Draft', 'Table section title for draft quotes');
    register.add(Id.activeTableTitle, 'Active', 'Table section title for active quotes');
    register.add(Id.issuedTableTitle, 'Issued', 'Table section title for issued quotes');
    register.add(Id.acceptedTableTitle, 'Accepted', 'Table section title for accepted quotes');
    register.add(Id.underReviewTableTitle, 'Under Review', 'Table section title for under review quotes');
    register.add(Id.reviewedTableTitle, 'Reviewed', 'Table section title for reviewed quotes');
    register.add(Id.archivedTableTitle, 'Archived', 'Table section title for archived quotes');

    // Toasts (add if you use them)
    // register.add(Id.quotesRefreshedToast, "Your Quotes have been refreshed", 'Toast: Quotes refreshed');
    // register.add(Id.updatesComingSoonToast, "Updates are coming soon", 'Toast: Updates coming soon');
    // register.add(Id.moreUpdatesComingToast, "More Updates are coming, refreshing soon", 'Toast: More updates coming');

    register.add(
      Id.quoteCopiedSuccessfully,
      "{{glossary 'quote'}} '{{quoteNumber}}' '{{quoteTitle}}' copy completed",
      'Toast shown when a quote finishes copying'
    );

    register.add(
      Id.quoteCopyFailed,
      "{{glossary 'quote'}} '{{quoteNumber}}' '{{quoteTitle}}' copy failed. {{errorMessage}}",
      'Toast shown when a quote copy fails'
    );

    // Add for Discard button
    register.add(Id.discardButton, 'Discard', 'Button label for discarding/cancelling an action');
    register.add(Id.copyButton, 'Copy', 'Button label for copying a quote');
    register.add(Id.deleteButton, 'Delete', 'Button label for deleting a quote');
    register.add(Id.termsButton, 'Choose', 'Button label for terms and conditions');
    register.add(
      Id.termsPickButton,
      "Select {{glossary 'terms-conditions'}}",
      'Button label for picking terms and conditions'
    );
    register.add(
      Id.termsClearButton,
      "Clear {{glossary 'terms-conditions'}}",
      'Button label for clearing terms and conditions'
    );
    register.add(Id.viewQuote, 'View Quote', 'Button label for opening a quote');

    // Tab titles
    register.add(Id.quoteDefaultsTab, 'Defaults', 'Tab title for quote defaults');
  }
}
